import type { components } from '@adiox/api';

export type OrderType = components['schemas']['OrderType'];
export type Side = components['schemas']['Side'];
type SymbolInfo = components['schemas']['SymbolInfo'];

/** The ways the ticket can place an order: at market, or one of the pending kinds. */
export const TICKET_KINDS = [
  'Market',
  'BuyLimit',
  'SellLimit',
  'BuyStop',
  'SellStop',
  'BuyStopLimit',
  'SellStopLimit',
] as const;
export type TicketKind = (typeof TICKET_KINDS)[number];

export const KIND_LABELS: Record<TicketKind, string> = {
  Market: 'Market execution',
  BuyLimit: 'Buy Limit',
  SellLimit: 'Sell Limit',
  BuyStop: 'Buy Stop',
  SellStop: 'Sell Stop',
  BuyStopLimit: 'Buy Stop Limit',
  SellStopLimit: 'Sell Stop Limit',
};

export const sideOfOrderType = (type: OrderType): Side => (type.startsWith('Buy') ? 'Buy' : 'Sell');

export interface QuoteLike {
  bid: number;
  ask: number;
  fresh: boolean;
  sessionOpen: boolean;
}

export interface TradeGate {
  hubState: string;
  feedUp: boolean | null;
  accountLoaded: boolean;
  tradingEnabled: boolean;
  quote: QuoteLike | undefined;
  symbol: Pick<SymbolInfo, 'tradeMode'> | undefined;
  /** An investor (read-only) session. */
  readOnly?: boolean;
}

/**
 * Why trading is not possible right now, or null when it is. The server refuses these cases too; checking here means the
 * user is told before they click, and no request is sent that is bound to fail.
 */
export function tradeBlockReason(g: TradeGate): string | null {
  if (g.readOnly) return 'Read-only view: this session signed in with the investor password.';
  if (!g.accountLoaded) return 'The account is still loading.';
  if (g.hubState !== 'connected') return 'Not connected. Trading resumes when the connection is back.';
  if (g.feedUp === false) return 'The price feed is down. Trading is paused until prices return.';
  if (!g.tradingEnabled) return 'Trading is disabled on this account.';
  if (!g.symbol) return 'Choose a symbol.';
  if (g.symbol.tradeMode === 'Disabled') return 'Trading is disabled for this symbol.';
  if (!g.quote) return 'Waiting for a price.';
  if (!g.quote.sessionOpen) return 'The market is closed for this symbol.';
  if (!g.quote.fresh) return 'The price is stale. Waiting for a fresh one.';
  return null;
}

/** Whether the symbol's trade mode allows opening this side (close-only allows neither). */
export function sideAllowed(tradeMode: string | undefined, side: Side): boolean {
  if (tradeMode === 'CloseOnly' || tradeMode === 'Disabled') return false;
  if (tradeMode === 'LongOnly') return side === 'Buy';
  if (tradeMode === 'ShortOnly') return side === 'Sell';
  return true;
}

const decimals = (step: number): number => {
  const text = step.toString();
  if (text.includes('e-')) return Number(text.split('e-')[1]);
  return text.includes('.') ? text.split('.')[1]!.length : 0;
};

/** Rounds a volume to the symbol's step (no float dust) and keeps it within its limits. */
export function normalizeVolume(
  volume: number,
  info: Pick<SymbolInfo, 'volumeMin' | 'volumeMax' | 'volumeStep'>,
) {
  if (!Number.isFinite(volume)) return info.volumeMin;
  const steps = Math.round((volume - info.volumeMin) / info.volumeStep);
  const snapped = info.volumeMin + Math.max(0, steps) * info.volumeStep;
  const clamped = Math.min(info.volumeMax, Math.max(info.volumeMin, snapped));
  return Number(clamped.toFixed(decimals(info.volumeStep)));
}

/** A message when the volume is not tradable as typed, otherwise null. */
export function volumeProblem(
  volume: number,
  info: Pick<SymbolInfo, 'volumeMin' | 'volumeMax' | 'volumeStep'>,
): string | null {
  if (!Number.isFinite(volume) || volume <= 0) return 'Enter a volume.';
  if (volume < info.volumeMin) return `The smallest volume is ${info.volumeMin}.`;
  if (volume > info.volumeMax) return `The largest volume is ${info.volumeMax}.`;
  const steps = (volume - info.volumeMin) / info.volumeStep;
  if (Math.abs(steps - Math.round(steps)) > 1e-6)
    return `The volume must be a multiple of ${info.volumeStep}.`;
  return null;
}

/** The price a pending order of this kind must respect relative to the market, as a hint before the server's check. */
export function pendingPriceProblem(kind: TicketKind, price: number, quote: Pick<QuoteLike, 'bid' | 'ask'>) {
  if (kind === 'Market') return null;
  if (!Number.isFinite(price) || price <= 0) return 'Enter a price.';
  switch (kind) {
    case 'BuyLimit':
      return price < quote.ask ? null : `A Buy Limit must be below the current ask (${quote.ask}).`;
    case 'SellLimit':
      return price > quote.bid ? null : `A Sell Limit must be above the current bid (${quote.bid}).`;
    case 'BuyStop':
    case 'BuyStopLimit':
      return price > quote.ask
        ? null
        : `A ${kind === 'BuyStop' ? 'Buy Stop' : 'Buy Stop Limit'} must be above the current ask (${quote.ask}).`;
    case 'SellStop':
    case 'SellStopLimit':
      return price < quote.bid
        ? null
        : `A ${kind === 'SellStop' ? 'Sell Stop' : 'Sell Stop Limit'} must be below the current bid (${quote.bid}).`;
  }
}

/** The stop-limit price sits on the other side of the trigger: below it for a buy, above it for a sell. */
export function stopLimitProblem(kind: TicketKind, trigger: number, limit: number): string | null {
  if (kind !== 'BuyStopLimit' && kind !== 'SellStopLimit') return null;
  if (!Number.isFinite(limit) || limit <= 0) return 'Enter the limit price.';
  if (kind === 'BuyStopLimit')
    return limit < trigger ? null : 'The limit price must be below the stop price.';
  return limit > trigger ? null : 'The limit price must be above the stop price.';
}

/** Stops must be on the right side of the entry, as a hint: SL/TP for a buy are below/above the entry price. */
export function stopsProblem(side: Side, entry: number, stopLoss: number, takeProfit: number): string | null {
  if (stopLoss > 0 && (side === 'Buy' ? stopLoss >= entry : stopLoss <= entry)) {
    return side === 'Buy'
      ? 'The stop loss must be below the price for a buy.'
      : 'The stop loss must be above the price for a sell.';
  }
  if (takeProfit > 0 && (side === 'Buy' ? takeProfit <= entry : takeProfit >= entry)) {
    return side === 'Buy'
      ? 'The take profit must be above the price for a buy.'
      : 'The take profit must be below the price for a sell.';
  }
  return null;
}

const MESSAGES: Record<string, string> = {
  Invalid: 'The request is not valid.',
  InvalidVolume: 'The volume is not allowed for this symbol.',
  InvalidPrice: 'The price is not valid.',
  InvalidStops: 'The stop loss or take profit is too close to the price, or on the wrong side.',
  TradeDisabled: 'Trading is disabled for this symbol.',
  MarketClosed: 'The market is closed.',
  NoMoney: 'Not enough free margin for this order.',
  PriceOff: 'There is no current price for this symbol.',
  InvalidExpiration: 'The expiration is not valid.',
  NoChanges: 'Nothing was changed.',
  Frozen: 'The order or position is frozen because the price is too close to it.',
  LimitOrders: 'The limit on pending orders was reached.',
  InvalidOrder: 'That order no longer exists.',
  PositionClosed: 'That position is already closed.',
  InvalidCloseVolume: 'The volume to close is not valid.',
  LongOnly: 'Only buy positions are allowed for this symbol.',
  ShortOnly: 'Only sell positions are allowed for this symbol.',
  CloseOnly: 'Only closing positions is allowed for this symbol.',
};

export interface Rejection {
  message: string;
  retcodeName?: string;
}

/** Turns a server problem-details body (or a transport failure) into a message for the user. */
export function describeRejection(status: number | undefined, body: unknown): Rejection {
  const problem = (typeof body === 'object' && body !== null ? body : {}) as {
    retcodeName?: string;
    detail?: string;
    title?: string;
  };
  if (problem.retcodeName && MESSAGES[problem.retcodeName]) {
    return { message: MESSAGES[problem.retcodeName]!, retcodeName: problem.retcodeName };
  }
  if (status === 429) return { message: 'Too many requests. Wait a moment and try again.' };
  if (status === 503)
    return { message: 'The trading engine is unavailable. Nothing was executed; try again shortly.' };
  if (status === 401 || status === 403)
    return { message: 'Your session does not allow this. Sign in again.' };
  if (status === 404) return { message: 'That item no longer exists.' };
  if (problem.detail) return { message: problem.detail, retcodeName: problem.retcodeName };
  return { message: 'The request failed. Check the trade list before trying again.' };
}

/** Whether a failed attempt may be repeated with the same idempotency key (nothing was decided by the server). */
export const isRetryableStatus = (status: number | undefined): boolean =>
  status === undefined || status === 429 || status === 502 || status === 503 || status === 504;

export const newIdempotencyKey = (): string =>
  globalThis.crypto?.randomUUID?.() ?? `k-${Date.now()}-${Math.random().toString(16).slice(2)}`;
